// Store detail (WEB_PLAN.md §5.6, route /stores/:id): port of RecordDetailView.swift, RecordDetailTabs.swift,
// RecordInfoTab.swift and RecordFilesTab.swift, read-only in Phase 1. Header with a mini map, then four tabs:
// History (historyEntries + the iOS filters), Upcoming (scheduled activities), Info (every field) and Files.
//
// The store is looked up among the viewer's visible stores (archived included), so a rep opening a link to a store
// they cannot see gets "Store not found", as on iOS. History includes activities and messages an iPhone could not
// decode, marked "Newer app", so the office sees that something happened even before the console catches up.
// The tab is in the URL (?tab=files) so a link can open it; "Stores" goes back to the list view it came from.
//
// The route element is reused when one store links to another (a contact's account), so the view is keyed by the
// store id: the tab filter, "Show more", an open photo and the mini map's camera all start fresh for the new store.

import { CalendarClock, ChevronLeft, Clock, FolderOpen, Info, SearchX } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import type { ActivityPayload, AttachmentPayload, CommPayload, RecordPayload, UserPayload } from '@/contracts/models';
import { idKey } from '@/contracts/uuid';
import { parseWireDate } from '@/contracts/wireDate';
import {
  useActivities,
  useAttachments,
  useComms,
  useCustomFields,
  useLeadSources,
  useSettings,
  useTerritories,
  useVisibleRecords,
  type DataItem,
  type DataList,
} from '@/data/hooks';
import { statsOf } from '@/domain/derived';
import { formatGrouped, formatShortDate, formatShortDateTime } from '@/domain/format';
import {
  HISTORY_FILTERS,
  HISTORY_FILTER_TITLES,
  activityDisplayTitle,
  historyEntries,
  historyFilterMatches,
  recordDisplayName,
  upcomingActivities,
  type HistoryFilter,
} from '@/domain/records';
import { fullName, lookup, userName } from '@/domain/util';
import { RequireFirstSync, ScreenLoading, useDocumentTitle, useStoreRowLookups } from '@/features/common';
import { useCan } from '@/session/permissions';
import { Button, Chip, EmptyState, PageContainer, SectionCard, Tabs, cardClass, cn, useTicker, type TabItem } from '@/ui';
import { ActivityEntry, MessageEntry, type ActivityEntryProps } from './ActivityEntries';
import { recordFiles } from './detailModel';
import { DocumentRow, PhotoTile, PhotoViewer } from './FileTiles';
import { StoreHeader } from './StoreHeader';
import { storesBackHref } from './listState';
import { StoreInfoTab } from './StoreInfoTab';

type DetailTab = 'history' | 'upcoming' | 'info' | 'files';
const DETAIL_TABS: readonly DetailTab[] = ['history', 'upcoming', 'info', 'files'];
const isDetailTab = (v: string | null): v is DetailTab => v !== null && (DETAIL_TABS as readonly string[]).includes(v);

/** History renders this many entries at a time (a store visited daily for years has thousands). */
const HISTORY_PAGE = 100;

export default function StoreDetailPage() {
  const { id = '' } = useParams();
  return (
    <RequireFirstSync
      what="Store"
      fallback={
        <PageContainer width="default">
          <ScreenLoading layout="list" count={4} label="Loading store…" />
        </PageContainer>
      }
    >
      <StoreDetail id={id} />
    </RequireFirstSync>
  );
}

function StoreDetail({ id }: { id: string }) {
  const can = useCan();
  const location = useLocation();
  const visible = useVisibleRecords({ includeArchived: true });
  const item = visible.get(id);
  useDocumentTitle(item ? recordDisplayName(item.value) : 'Store');

  // Back to the exact list view the store was opened from (its query), else the plain list.
  const back = { to: storesBackHref(location.state), label: can.isRep ? 'My stores' : 'Stores' };

  return (
    <PageContainer width="default" className="flex flex-col gap-5">
      <Link
        to={back.to}
        className="-ml-1 inline-flex w-fit items-center gap-0.5 rounded-md px-1 text-sm font-medium text-link hover:underline"
      >
        <ChevronLeft aria-hidden className="size-4" />
        {back.label}
      </Link>
      {item ? (
        <StoreView key={idKey(item.id)} item={item} visible={visible} />
      ) : (
        <EmptyState
          icon={<SearchX />}
          title="Store not found"
          message="This store was deleted or is no longer visible to you."
          action={
            <Link to={back.to} className="text-sm font-semibold text-link hover:underline">
              Back to {back.label.toLowerCase()}
            </Link>
          }
        />
      )}
    </PageContainer>
  );
}

function StoreView({ item, visible }: { item: DataItem<RecordPayload>; visible: DataList<RecordPayload> }) {
  const can = useCan();
  const location = useLocation();
  const record = item.value;
  const lookups = useStoreRowLookups();
  const settings = useSettings();
  const activities = useActivities();
  const comms = useComms();
  const attachments = useAttachments();
  const customFields = useCustomFields();
  const territories = useTerritories();
  const sources = useLeadSources();
  const minute = useTicker(60_000);
  const now = useMemo(() => {
    void minute;
    return new Date();
  }, [minute]);

  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: DetailTab = isDetailTab(tabParam) ? tabParam : 'history';
  // setSearchParams navigates, and a navigation without `state` drops it: keep the list query the "Stores" link needs.
  const setTab = useCallback(
    (next: DetailTab) => {
      const p = new URLSearchParams(params);
      if (next === 'history') p.delete('tab');
      else p.set('tab', next);
      setParams(p, { replace: true, state: location.state });
    },
    [params, setParams, location.state],
  );

  // Best-effort values of every row (newer-app items included, badged); each list recomputes only when its
  // collection changes.
  const activityValues = useMemo(() => activities.items.map((i) => i.value), [activities]);
  const commValues = useMemo(() => comms.items.map((i) => i.value), [comms]);
  const history = useMemo(() => historyEntries(record.id, activityValues, commValues), [record.id, activityValues, commValues]);
  const upcoming = useMemo(() => upcomingActivities(record.id, activityValues), [record.id, activityValues]);
  const files = useMemo(() => recordFiles(record.id, attachments.items), [record.id, attachments]);
  const stats = statsOf(lookups.stats, record.id);
  const territory = territories.get(record.territoryId)?.value;
  const source = sources.get(record.sourceId)?.value;
  const parent = record.parentRecordId ? visible.get(record.parentRecordId) : undefined;

  const [photo, setPhoto] = useState<DataItem<AttachmentPayload> | null>(null);
  const photoDetails = useMemo(() => {
    if (!photo) return [];
    const a = photo.value;
    const added = parseWireDate(a.createdAt);
    const loggedWith = a.activityId ? activities.get(a.activityId)?.value : undefined;
    const rows = [
      { label: 'Added', value: added ? formatShortDateTime(added, now) : null },
      { label: 'By', value: userName(lookups.users, a.uploadedBy) },
    ];
    if (loggedWith) rows.push({ label: 'Logged with', value: activityDisplayTitle(loggedWith, lookups.visitResults) });
    return rows;
  }, [photo, activities, lookups.users, lookups.visitResults, now]);

  const fileCount = files.photos.length + files.documents.length;
  const tabs: TabItem<DetailTab>[] = [
    { value: 'history', label: 'History', icon: <Clock />, count: history.length },
    { value: 'upcoming', label: 'Upcoming', icon: <CalendarClock />, count: upcoming.length },
    { value: 'info', label: 'Info', icon: <Info /> },
    { value: 'files', label: 'Files', icon: <FolderOpen />, count: fileCount },
  ];

  const entryProps = {
    users: lookups.users,
    visitResults: lookups.visitResults,
    attachments,
    distanceUnit: settings.distanceUnit,
    now,
    onOpenPhoto: setPhoto,
  };

  return (
    <>
      <StoreHeader
        item={item}
        stats={stats}
        lookups={lookups}
        territory={territory}
        source={source}
        colorize={settings.defaultMapColorize}
        linkTerritory={can.isManager}
        now={now}
      />

      {record.doNotContact && (
        <p role="note" className="rounded-tile bg-danger-soft px-4 py-3 text-sm font-medium text-danger-text">
          This store is flagged Do Not Contact. Check before calling or texting.
        </p>
      )}

      <Tabs value={tab} onValueChange={setTab} items={tabs} label="Store details">
        {(active) => {
          switch (active) {
            case 'history':
              return (
                <HistoryTab
                  entries={history}
                  activityItem={(aid) => activities.get(aid)}
                  commItem={(cid) => comms.get(cid)}
                  entryProps={entryProps}
                />
              );
            case 'upcoming':
              return (
                <UpcomingTab upcoming={upcoming} activityItem={(aid) => activities.get(aid)} entryProps={entryProps} />
              );
            case 'info':
              return (
                <StoreInfoTab
                  item={item}
                  lookups={lookups}
                  territory={territory}
                  source={source}
                  parent={parent}
                  customFields={customFields.values}
                  linkTerritory={can.isManager}
                  now={now}
                />
              );
            case 'files':
              return <FilesTab files={files} onOpenPhoto={setPhoto} users={lookups.users} />;
          }
        }}
      </Tabs>

      {photo && <PhotoViewer item={photo} onClose={() => setPhoto(null)} details={photoDetails} />}
    </>
  );
}

type EntryProps = Omit<ActivityEntryProps, 'activity' | 'item'>;

function HistoryTab({
  entries,
  activityItem,
  commItem,
  entryProps,
}: {
  entries: ReturnType<typeof historyEntries>;
  activityItem: (id: string) => DataItem<ActivityPayload> | undefined;
  commItem: (id: string) => DataItem<CommPayload> | undefined;
  entryProps: EntryProps;
}) {
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [limit, setLimit] = useState(HISTORY_PAGE);
  const shown = useMemo(() => entries.filter((e) => historyFilterMatches(filter, e)), [entries, filter]);

  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label="Show" className="flex flex-wrap gap-2">
        {HISTORY_FILTERS.map((f) => (
          <Chip
            key={f}
            color="#102A5E"
            selected={filter === f}
            onClick={() => {
              setFilter(f);
              setLimit(HISTORY_PAGE);
            }}
            className="tracking-[0.04em] uppercase"
          >
            {HISTORY_FILTER_TITLES[f]}
          </Chip>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className={cardClass}>
          <EmptyState
            compact
            icon={<Clock />}
            title={filter === 'all' ? 'No history yet' : 'Nothing here'}
            message={filter === 'all' ? 'Visits, calls, notes and stage changes will show up here.' : 'Try another filter.'}
          />
        </div>
      ) : (
        <div className={cn(cardClass, 'px-4')}>
          <ul aria-label="History" className="divide-y divide-line">
            {shown.slice(0, limit).map((e) =>
              e.kind === 'activity' ? (
                <ActivityEntry key={e.id} activity={e.activity} item={activityItem(e.id)} {...entryProps} />
              ) : (
                <MessageEntry key={e.id} comm={e.comm} item={commItem(e.id)} now={entryProps.now} />
              ),
            )}
          </ul>
          {shown.length > limit && (
            <div className="flex justify-center border-t border-line py-3">
              <Button variant="ghost" onClick={() => setLimit((n) => n + HISTORY_PAGE)}>
                Show {formatGrouped(Math.min(HISTORY_PAGE, shown.length - limit))} more of {formatGrouped(shown.length - limit)}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UpcomingTab({
  upcoming,
  activityItem,
  entryProps,
}: {
  upcoming: readonly ActivityPayload[];
  activityItem: (id: string) => DataItem<ActivityPayload> | undefined;
  entryProps: EntryProps;
}) {
  if (upcoming.length === 0) {
    return (
      <div className={cardClass}>
        <EmptyState
          compact
          icon={<CalendarClock />}
          title="Nothing scheduled"
          message="Follow-up visits, calls and tasks scheduled for this store show up here."
        />
      </div>
    );
  }
  return (
    <div className={cn(cardClass, 'px-4')}>
      <ul aria-label="Upcoming" className="divide-y divide-line">
        {upcoming.map((a) => (
          <ActivityEntry key={a.id} activity={a} item={activityItem(a.id)} {...entryProps} />
        ))}
      </ul>
    </div>
  );
}

function FilesTab({
  files,
  onOpenPhoto,
  users,
}: {
  files: ReturnType<typeof recordFiles>;
  onOpenPhoto: (item: DataItem<AttachmentPayload>) => void;
  users: ReadonlyMap<string, UserPayload>;
}) {
  if (files.photos.length === 0 && files.documents.length === 0) {
    return (
      <div className={cardClass}>
        <EmptyState
          compact
          icon={<FolderOpen />}
          title="No files yet"
          message="Photos of the property, signed paperwork or business cards attach here and show in the store's history."
        />
      </div>
    );
  }
  const n = files.photos.length;
  return (
    <div className="flex flex-col gap-4">
      {n > 0 && (
        <SectionCard title={`${formatGrouped(n)} photo${n === 1 ? '' : 's'}`}>
          <ul className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">
            {files.photos.map((p) => (
              <li key={p.id}>
                <PhotoTile item={p} onOpen={onOpenPhoto} className="w-full" />
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
      {files.documents.length > 0 && (
        <SectionCard title="Documents">
          <ul className="divide-y divide-line">
            {files.documents.map((d) => {
              const added = parseWireDate(d.value.createdAt);
              const by = lookup(users, d.value.uploadedBy);
              const meta = [added ? formatShortDate(added) : null, by ? fullName(by) : null].filter(Boolean).join(' · ');
              return <DocumentRow key={d.id} item={d} meta={meta} />;
            })}
          </ul>
        </SectionCard>
      )}
    </div>
  );
}
